import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, Trash2, Copy, Search, Loader2, Video, X, Check,
  Pencil, RefreshCw, Play, ImageIcon, Filter, Grid3x3, List as ListIcon,
  AlertTriangle, ExternalLink, FolderOpen,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_SCOPES = [
  { key: "home", label: "Accueil" },
  { key: "hero", label: "Hero / Bannière principale" },
  { key: "banner", label: "Bannières secondaires" },
  { key: "product", label: "Produits" },
  { key: "collection", label: "Collections" },
  { key: "about", label: "À propos / La Maison" },
  { key: "backstage", label: "Coulisses" },
  { key: "packaging", label: "Packaging" },
  { key: "founder", label: "Fondateur" },
  { key: "shipping", label: "Envois" },
  { key: "other", label: "Autre" },
];

function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso.slice(0, 10); }
}

/** Full-screen preview lightbox */
const PreviewModal = ({ asset, onClose }) => {
  if (!asset) return null;
  const src = `${BASE}${asset.url}`;
  return (
    <div className="fixed inset-0 bg-black/95 z-[200] flex flex-col" data-testid="preview-modal" onClick={onClose}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10" onClick={(e) => e.stopPropagation()}>
        <div className="min-w-0 flex-1 mr-4">
          <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-1">Aperçu · {asset.kind === "video" ? "VIDÉO" : "IMAGE"}</p>
          <p className="font-serif text-lg text-white truncate">{asset.title || asset.filename}</p>
          <p className="text-white/40 text-xs mt-1">{fmtSize(asset.size)} · {asset.mime_type} · {fmtDate(asset.uploaded_at)}</p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={src} target="_blank" rel="noreferrer"
            className="border border-white/15 hover:border-[color:var(--gold)] text-white/70 hover:text-[color:var(--gold)] px-3 py-2 label-caps text-[10px] tracking-[0.2em] flex items-center gap-1.5"
            data-testid="preview-open-new-tab"
          >
            <ExternalLink size={12} /> Nouvel onglet
          </a>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white p-2 border border-white/15 hover:border-white"
            data-testid="preview-close"
          >
            <X size={16} />
          </button>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center overflow-hidden p-6" onClick={(e) => e.stopPropagation()}>
        {asset.kind === "video" ? (
          <video src={src} controls autoPlay playsInline className="max-h-full max-w-full" data-testid="preview-video" />
        ) : (
          <img src={src} alt={asset.alt_text || asset.filename} className="max-h-full max-w-full object-contain" data-testid="preview-image" />
        )}
      </div>
    </div>
  );
};

/** Edit metadata modal (title / alt / scope) */
const EditModal = ({ asset, onClose, onSaved, scopes }) => {
  const [form, setForm] = useState({ title: "", alt_text: "", scope: "other" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (asset) setForm({ title: asset.title || "", alt_text: asset.alt_text || "", scope: asset.scope || "other" });
  }, [asset]);

  if (!asset) return null;

  const save = async () => {
    setSaving(true);
    try {
      const url = asset.source === "library"
        ? `${API}/admin/media-library/${asset.id}`
        : `${API}/admin/media-library/uploads/${asset.id}`;
      await axios.patch(url, form, { withCredentials: true });
      toast.success("Média mis à jour");
      onSaved?.(asset.id, form);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mise à jour impossible");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-[150] flex items-center justify-center p-4" data-testid="edit-modal">
      <div className="bg-[#0A0A0A] border border-[color:var(--gold)]/30 w-full max-w-2xl">
        <div className="flex items-center justify-between border-b border-white/10 p-5">
          <div>
            <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-1">Édition</p>
            <p className="font-serif text-xl">Métadonnées du média</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white p-2" data-testid="edit-close"><X size={18} /></button>
        </div>
        <div className="p-6 grid md:grid-cols-[200px_1fr] gap-6">
          <div className="aspect-square bg-[#0d0d0d] border border-white/10 flex items-center justify-center overflow-hidden">
            {asset.kind === "video"
              ? <div className="flex flex-col items-center gap-2 text-white/40"><Video size={40} /><span className="text-xs">Vidéo</span></div>
              : <img src={`${BASE}${asset.url}`} alt="" className="w-full h-full object-cover" />}
          </div>
          <div className="space-y-4">
            <div>
              <label className="label-caps text-[10px] text-white/50 tracking-[0.25em] mb-2 block">Titre</label>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full bg-transparent border border-white/15 focus:border-[color:var(--gold)] px-3 py-2 text-sm outline-none"
                placeholder="Ex : Hero Accueil — Hiver 2026"
                data-testid="edit-title"
              />
            </div>
            <div>
              <label className="label-caps text-[10px] text-white/50 tracking-[0.25em] mb-2 block">Texte alternatif (SEO / accessibilité)</label>
              <textarea
                value={form.alt_text}
                onChange={(e) => setForm({ ...form, alt_text: e.target.value })}
                rows={2}
                className="w-full bg-transparent border border-white/15 focus:border-[color:var(--gold)] px-3 py-2 text-sm outline-none"
                placeholder="Ex : Flacon de parfum sur velours noir"
                data-testid="edit-alt"
              />
            </div>
            <div>
              <label className="label-caps text-[10px] text-white/50 tracking-[0.25em] mb-2 block">Emplacement (dossier)</label>
              <select
                value={form.scope}
                onChange={(e) => setForm({ ...form, scope: e.target.value })}
                className="w-full bg-black border border-white/15 focus:border-[color:var(--gold)] px-3 py-2 text-sm outline-none"
                data-testid="edit-scope"
              >
                {scopes.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div className="pt-2 flex gap-2 justify-end">
              <button onClick={onClose} className="border border-white/15 text-white/70 hover:text-white px-4 py-2 label-caps text-[10px] tracking-[0.25em]" data-testid="edit-cancel">Annuler</button>
              <button
                onClick={save} disabled={saving}
                className="bg-[color:var(--gold)] text-black hover:bg-white px-6 py-2 label-caps text-[10px] tracking-[0.25em] disabled:opacity-50 flex items-center gap-2"
                data-testid="edit-save"
              >
                {saving ? <><Loader2 size={12} className="animate-spin" /> Enregistrement</> : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Delete confirmation with usage list */
const DeleteModal = ({ asset, onClose, onDeleted }) => {
  const [usage, setUsage] = useState(null);
  const [checking, setChecking] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!asset) return;
    setChecking(true); setUsage(null);
    axios.get(`${API}/admin/media-library/${asset.id}/usage`, { withCredentials: true })
      .then((r) => setUsage(r.data))
      .catch(() => setUsage({ total: 0 }))
      .finally(() => setChecking(false));
  }, [asset]);

  if (!asset) return null;

  const doDelete = async () => {
    setDeleting(true);
    try {
      const url = asset.source === "library"
        ? `${API}/admin/media-library/${asset.id}?force=true`
        : `${API}/admin/media-library/uploads/${asset.id}?force=true`;
      await axios.delete(url, { withCredentials: true });
      toast.success("Média supprimé");
      onDeleted?.(asset.id);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Suppression impossible");
    } finally { setDeleting(false); }
  };

  const totalUsage = usage?.total || 0;

  return (
    <div className="fixed inset-0 bg-black/85 z-[150] flex items-center justify-center p-4" data-testid="delete-modal">
      <div className="bg-[#0A0A0A] border border-red-500/40 w-full max-w-lg">
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <AlertTriangle size={22} className="text-red-400" />
            <p className="font-serif text-xl">Supprimer définitivement ?</p>
          </div>
          <p className="text-white/60 text-sm mt-3">
            « <span className="text-white">{asset.title || asset.filename}</span> » sera supprimé du site.
            Cette action est <span className="text-red-400">irréversible</span>.
          </p>
        </div>
        <div className="p-6">
          {checking ? (
            <p className="text-white/40 text-sm flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Vérification des références…</p>
          ) : totalUsage === 0 ? (
            <div className="text-emerald-400/80 text-sm flex items-start gap-2">
              <Check size={14} className="mt-0.5" />
              <span>Ce média n'est utilisé nulle part sur le site. Suppression sûre.</span>
            </div>
          ) : (
            <div className="border border-amber-400/30 bg-amber-500/5 p-4 text-sm">
              <p className="text-amber-400 mb-3 flex items-center gap-2"><AlertTriangle size={14} /> {totalUsage} référence(s) trouvée(s) :</p>
              {usage.products?.length > 0 && (
                <div className="mb-2"><p className="text-white/40 text-xs mb-1">Produits</p>
                  <ul className="text-white/80 text-xs space-y-0.5">{usage.products.map((p) => <li key={p.id}>· {p.name}</li>)}</ul>
                </div>
              )}
              {usage.media?.length > 0 && (
                <div className="mb-2"><p className="text-white/40 text-xs mb-1">Coulisses</p>
                  <ul className="text-white/80 text-xs space-y-0.5">{usage.media.map((m) => <li key={m.id}>· {m.title}</li>)}</ul>
                </div>
              )}
              {usage.content_blocks?.length > 0 && (
                <div><p className="text-white/40 text-xs mb-1">Contenu / CMS</p>
                  <ul className="text-white/80 text-xs space-y-0.5">{usage.content_blocks.map((c) => <li key={c.key}>· {c.key}</li>)}</ul>
                </div>
              )}
              <p className="text-white/50 text-xs mt-3">Ces zones afficheront un lien cassé après suppression. Remplacez le fichier ou modifiez la fiche produit avant.</p>
            </div>
          )}
        </div>
        <div className="p-6 pt-2 flex gap-2 justify-end">
          <button onClick={onClose} className="border border-white/15 text-white/70 hover:text-white px-4 py-2 label-caps text-[10px] tracking-[0.25em]" data-testid="delete-cancel">Annuler</button>
          <button
            onClick={doDelete} disabled={deleting}
            className="bg-red-500 text-white hover:bg-red-400 px-6 py-2 label-caps text-[10px] tracking-[0.25em] disabled:opacity-50 flex items-center gap-2"
            data-testid="delete-confirm"
          >
            {deleting ? <><Loader2 size={12} className="animate-spin" /> Suppression</> : <><Trash2 size={12} /> Supprimer définitivement</>}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Media Library — full manager (default) or picker */
const MediaLibrary = ({ picker = false, onSelect, filterKind = "" }) => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [scopes, setScopes] = useState(DEFAULT_SCOPES);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [scope, setScope] = useState("");
  const [kind, setKind] = useState(filterKind);
  const [q, setQ] = useState("");
  const [uploadScope, setUploadScope] = useState("home");
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [view, setView] = useState("grid");
  const fileRef = useRef(null);
  const replaceRef = useRef(null);
  const [replacingId, setReplacingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (scope) params.scope = scope;
      if (kind) params.kind = kind;
      if (q) params.q = q;
      const [{ data: d }, { data: s }] = await Promise.all([
        axios.get(`${API}/admin/media-library/all`, { params, withCredentials: true }),
        axios.get(`${API}/admin/media-library/stats`, { withCredentials: true }),
      ]);
      setItems(d.items || []);
      setStats(s);
      if (d.scopes && d.scope_labels) {
        setScopes(d.scopes.map((k) => ({ key: k, label: d.scope_labels[k] || k })));
      }
    } catch { toast.error("Chargement impossible"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, kind]);
  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { load(); /* eslint-disable-next-line */ }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let ok = 0;
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("scope", uploadScope);
        await axios.post(`${API}/admin/media-library/upload`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok++;
      }
      toast.success(`${ok} fichier${ok > 1 ? "s" : ""} envoyé${ok > 1 ? "s" : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload échoué");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const doReplace = async (file) => {
    if (!file || !replacingId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.put(`${API}/admin/media-library/${replacingId}/replace`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Fichier remplacé — les références restent intactes");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Remplacement impossible");
    } finally {
      setUploading(false);
      setReplacingId(null);
      if (replaceRef.current) replaceRef.current.value = "";
    }
  };

  const copyUrl = (url) => {
    const full = `${BASE}${url}`;
    navigator.clipboard.writeText(full).then(
      () => toast.success("URL copiée"),
      () => toast.error("Copie impossible")
    );
  };

  const toggleSelect = (assetKey) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(assetKey)) n.delete(assetKey); else n.add(assetKey);
      return n;
    });
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Supprimer les ${selected.size} médias sélectionnés ? Action irréversible.`)) return;
    let ok = 0, fail = 0;
    for (const key of selected) {
      const [source, id] = key.split(":");
      try {
        const url = source === "library"
          ? `${API}/admin/media-library/${id}?force=true`
          : `${API}/admin/media-library/uploads/${id}?force=true`;
        await axios.delete(url, { withCredentials: true });
        ok++;
      } catch { fail++; }
    }
    toast[fail === 0 ? "success" : "warning"](`${ok} supprimés${fail > 0 ? `, ${fail} en échec` : ""}`);
    setSelected(new Set());
    load();
  };

  const filteredCount = items.length;

  return (
    <div data-testid="media-library">
      {/* Stats strip */}
      {!picker && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="library-stats">
          <StatCard label="Total médias" value={stats.total} icon={FolderOpen} />
          <StatCard label="Images" value={stats.images} icon={ImageIcon} />
          <StatCard label="Vidéos" value={stats.videos} icon={Video} />
          <StatCard label="Emplacements" value={Object.values(stats.per_scope || {}).filter((c) => c > 0).length} icon={Filter} />
        </div>
      )}

      {/* Scope tabs (folders) */}
      {!picker && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="scope-tabs">
          <ScopeTab active={scope === ""} onClick={() => setScope("")} label="Tous" count={stats?.total} />
          {scopes.map((s) => (
            <ScopeTab
              key={s.key}
              active={scope === s.key}
              onClick={() => setScope(s.key)}
              label={s.label}
              count={stats?.per_scope?.[s.key] ?? 0}
            />
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap gap-3 items-center mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher par nom, titre, alt…"
            className="w-full bg-transparent border border-white/15 focus:border-[color:var(--gold)] pl-9 pr-3 py-2 outline-none text-sm"
            data-testid="lib-search"
          />
        </div>
        {!filterKind && (
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="bg-black border border-white/15 px-3 py-2 text-sm" data-testid="lib-kind">
            <option value="">Tous types</option>
            <option value="image">Images</option>
            <option value="video">Vidéos</option>
          </select>
        )}
        {!picker && (
          <div className="flex items-center gap-1 border border-white/15">
            <button onClick={() => setView("grid")} className={`p-2 ${view === "grid" ? "bg-[color:var(--gold)] text-black" : "text-white/50"}`} data-testid="view-grid" aria-label="Grille">
              <Grid3x3 size={14} />
            </button>
            <button onClick={() => setView("list")} className={`p-2 ${view === "list" ? "bg-[color:var(--gold)] text-black" : "text-white/50"}`} data-testid="view-list" aria-label="Liste">
              <ListIcon size={14} />
            </button>
          </div>
        )}
        {selected.size > 0 && !picker && (
          <button
            onClick={bulkDelete}
            className="border border-red-500/50 text-red-400 hover:bg-red-500 hover:text-white px-4 py-2 label-caps text-[10px] tracking-[0.25em] flex items-center gap-2"
            data-testid="bulk-delete"
          >
            <Trash2 size={12} /> Supprimer ({selected.size})
          </button>
        )}
      </div>

      {/* Upload zone */}
      {!picker && (
        <div className="border-2 border-dashed border-white/15 hover:border-[color:var(--gold)] transition-colors p-6 mb-8 text-center" data-testid="upload-zone">
          <input
            ref={fileRef} type="file" hidden multiple
            accept="image/jpeg,image/png,image/webp,image/heic,image/gif,image/avif,video/mp4,video/quicktime,video/webm"
            onChange={(e) => upload(Array.from(e.target.files || []))}
          />
          <input
            ref={replaceRef} type="file" hidden
            accept="image/jpeg,image/png,image/webp,image/heic,image/gif,image/avif,video/mp4,video/quicktime,video/webm"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) doReplace(f); }}
          />
          <div className="flex flex-col items-center gap-3">
            {uploading ? <Loader2 size={28} className="text-[color:var(--gold)] animate-spin" /> : <Upload size={28} className="text-[color:var(--gold)]" strokeWidth={1.5} />}
            <p className="text-white/70">Téléverser des photos ou vidéos</p>
            <p className="text-white/40 text-xs">JPG / PNG / WebP / HEIC / GIF / MP4 / MOV / WebM — max 200 Mo</p>
            <div className="flex items-center gap-3 mt-2">
              <label className="label-caps text-[10px] text-white/50">Emplacement :</label>
              <select value={uploadScope} onChange={(e) => setUploadScope(e.target.value)} className="bg-black border border-white/15 px-3 py-1.5 text-xs" data-testid="upload-scope">
                {scopes.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="upload-trigger"
              className="mt-3 bg-[color:var(--gold)] text-black hover:bg-white px-6 py-2 label-caps text-[10px] tracking-[0.25em] disabled:opacity-40"
            >
              Choisir les fichiers
            </button>
          </div>
        </div>
      )}

      {/* Results header */}
      <p className="text-white/40 text-xs mb-3" data-testid="results-count">
        {loading ? "Chargement…" : `${filteredCount} média${filteredCount > 1 ? "s" : ""} affiché${filteredCount > 1 ? "s" : ""}`}
      </p>

      {/* Empty state */}
      {!loading && items.length === 0 && (
        <div className="text-center py-16 border border-dashed border-white/10" data-testid="empty-state">
          <p className="text-white/50">Aucun média dans cette catégorie.</p>
          {!picker && <p className="text-white/30 text-xs mt-1">Uploadez ci-dessus ou changez de filtre.</p>}
        </div>
      )}

      {/* Grid view */}
      {view === "grid" && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="media-grid">
          {items.map((a) => (
            <AssetCard
              key={`${a.source}:${a.id}`} a={a}
              picker={picker} onSelect={onSelect}
              onPreview={() => setPreview(a)}
              onEdit={() => setEditing(a)}
              onDelete={() => setDeleting(a)}
              onCopyUrl={() => copyUrl(a.url)}
              onReplace={() => { setReplacingId(a.id); replaceRef.current?.click(); }}
              checked={selected.has(`${a.source}:${a.id}`)}
              onToggleSelect={() => toggleSelect(`${a.source}:${a.id}`)}
            />
          ))}
        </div>
      )}

      {/* List view */}
      {view === "list" && !picker && (
        <div className="border border-white/10 divide-y divide-white/5" data-testid="media-list">
          {items.map((a) => (
            <AssetRow
              key={`${a.source}:${a.id}`} a={a}
              onPreview={() => setPreview(a)}
              onEdit={() => setEditing(a)}
              onDelete={() => setDeleting(a)}
              onCopyUrl={() => copyUrl(a.url)}
              onReplace={() => { setReplacingId(a.id); replaceRef.current?.click(); }}
              checked={selected.has(`${a.source}:${a.id}`)}
              onToggleSelect={() => toggleSelect(`${a.source}:${a.id}`)}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      <PreviewModal asset={preview} onClose={() => setPreview(null)} />
      <EditModal asset={editing} onClose={() => setEditing(null)} onSaved={() => load()} scopes={scopes} />
      <DeleteModal asset={deleting} onClose={() => setDeleting(null)} onDeleted={() => load()} />
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon }) => (
  <div className="border border-white/10 bg-white/[0.02] p-4 flex items-center gap-3">
    <div className="border border-[color:var(--gold)]/30 p-2 text-[color:var(--gold)]"><Icon size={16} /></div>
    <div>
      <p className="text-2xl font-serif text-white">{value ?? 0}</p>
      <p className="text-[10px] text-white/40 tracking-[0.2em] uppercase">{label}</p>
    </div>
  </div>
);

const ScopeTab = ({ active, onClick, label, count }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 label-caps text-[10px] tracking-[0.2em] transition-colors border ${
      active
        ? "border-[color:var(--gold)] bg-[color:var(--gold)]/10 text-[color:var(--gold)]"
        : "border-white/10 text-white/50 hover:border-white/30 hover:text-white/80"
    }`}
    data-testid={`scope-tab-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
  >
    {label}{count !== undefined && count !== null && count > 0 && <span className="ml-2 text-white/40">{count}</span>}
  </button>
);

const AssetCard = ({ a, picker, onSelect, onPreview, onEdit, onDelete, onCopyUrl, onReplace, checked, onToggleSelect }) => (
  <div className="border border-white/10 hover:border-[color:var(--gold)] transition-colors group relative" data-testid={`asset-${a.id.slice(0, 8)}`}>
    {!picker && (
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggleSelect}
        className="absolute top-2 left-2 z-10 w-4 h-4 accent-[color:var(--gold)] cursor-pointer"
        aria-label="Sélectionner"
        data-testid={`select-${a.id.slice(0, 8)}`}
      />
    )}
    <button onClick={onPreview} className="block w-full aspect-square bg-[#0d0d0d] overflow-hidden relative group" data-testid={`preview-${a.id.slice(0, 8)}`}>
      {a.kind === "image" ? (
        <img src={`${BASE}${a.url}`} alt={a.alt_text || a.filename} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <>
          <video src={`${BASE}${a.url}#t=0.5`} muted preload="metadata" className="w-full h-full object-cover" />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
            <div className="w-12 h-12 rounded-full bg-[color:var(--gold)]/90 flex items-center justify-center">
              <Play size={20} className="text-black ml-1" fill="currentColor" />
            </div>
          </div>
        </>
      )}
      <span className="absolute bottom-2 left-2 bg-black/70 label-caps text-[8px] text-white/70 px-2 py-0.5 tracking-[0.2em]">{a.scope}</span>
      {a.kind === "video" && <span className="absolute top-2 right-2 bg-black/70 label-caps text-[8px] text-[color:var(--gold)] px-2 py-0.5">VIDÉO</span>}
      {a.source === "uploads" && <span className="absolute bottom-2 right-2 bg-black/70 label-caps text-[8px] text-white/50 px-2 py-0.5">CDN</span>}
    </button>
    <div className="p-2 space-y-0.5">
      <p className="text-[11px] text-white/90 truncate font-medium" title={a.filename}>{a.title || a.filename}</p>
      <p className="text-[9px] text-white/40">{fmtSize(a.size)} · {fmtDate(a.uploaded_at)}</p>
    </div>
    <div className="p-2 pt-0 flex flex-wrap gap-1">
      {picker ? (
        <button
          onClick={() => onSelect?.(a.url)}
          data-testid={`pick-${a.id.slice(0, 8)}`}
          className="flex-1 bg-[color:var(--gold)] text-black hover:bg-white px-2 py-1.5 label-caps text-[9px] flex items-center justify-center gap-1"
        >
          <Check size={10} /> Choisir
        </button>
      ) : (
        <>
          <button onClick={onEdit} className="border border-white/15 hover:border-[color:var(--gold)] text-white/60 hover:text-[color:var(--gold)] p-1.5" title="Modifier" data-testid={`edit-${a.id.slice(0, 8)}`}><Pencil size={11} /></button>
          {a.can_replace && <button onClick={onReplace} className="border border-white/15 hover:border-[color:var(--gold)] text-white/60 hover:text-[color:var(--gold)] p-1.5" title="Remplacer le fichier" data-testid={`replace-${a.id.slice(0, 8)}`}><RefreshCw size={11} /></button>}
          <button onClick={onCopyUrl} className="border border-white/15 hover:border-[color:var(--gold)] text-white/60 hover:text-[color:var(--gold)] p-1.5" title="Copier l'URL" data-testid={`copy-${a.id.slice(0, 8)}`}><Copy size={11} /></button>
          <button onClick={onDelete} className="border border-white/15 hover:border-red-400 text-white/40 hover:text-red-400 p-1.5 ml-auto" title="Supprimer" data-testid={`del-${a.id.slice(0, 8)}`}><Trash2 size={11} /></button>
        </>
      )}
    </div>
  </div>
);

const AssetRow = ({ a, onPreview, onEdit, onDelete, onCopyUrl, onReplace, checked, onToggleSelect }) => (
  <div className="grid grid-cols-[auto_60px_1fr_120px_100px_100px_auto] items-center gap-3 p-3 hover:bg-white/[0.02]" data-testid={`row-${a.id.slice(0, 8)}`}>
    <input type="checkbox" checked={checked} onChange={onToggleSelect} className="w-4 h-4 accent-[color:var(--gold)] cursor-pointer" aria-label="Sélectionner" />
    <button onClick={onPreview} className="w-14 h-14 bg-[#0d0d0d] overflow-hidden relative">
      {a.kind === "image"
        ? <img src={`${BASE}${a.url}`} alt="" className="w-full h-full object-cover" loading="lazy" />
        : <div className="w-full h-full flex items-center justify-center text-white/40"><Video size={18} /></div>}
    </button>
    <div className="min-w-0">
      <p className="text-sm text-white truncate">{a.title || a.filename}</p>
      <p className="text-[10px] text-white/40 truncate">{a.alt_text || "—"}</p>
    </div>
    <span className="label-caps text-[9px] bg-white/5 text-white/60 px-2 py-1 tracking-[0.2em] text-center">{a.scope}</span>
    <span className="text-[10px] text-white/50">{fmtSize(a.size)}</span>
    <span className="text-[10px] text-white/50">{fmtDate(a.uploaded_at)}</span>
    <div className="flex gap-1">
      <button onClick={onEdit} className="border border-white/15 hover:border-[color:var(--gold)] text-white/60 hover:text-[color:var(--gold)] p-1.5" title="Modifier"><Pencil size={11} /></button>
      {a.can_replace && <button onClick={onReplace} className="border border-white/15 hover:border-[color:var(--gold)] text-white/60 hover:text-[color:var(--gold)] p-1.5" title="Remplacer"><RefreshCw size={11} /></button>}
      <button onClick={onCopyUrl} className="border border-white/15 hover:border-[color:var(--gold)] text-white/60 hover:text-[color:var(--gold)] p-1.5" title="Copier URL"><Copy size={11} /></button>
      <button onClick={onDelete} className="border border-white/15 hover:border-red-400 text-white/40 hover:text-red-400 p-1.5" title="Supprimer"><Trash2 size={11} /></button>
    </div>
  </div>
);

export default MediaLibrary;

/**
 * MediaPicker — modal wrapper.
 * Usage: <MediaPicker open onClose onSelect={(url)=>...} kind="image" />
 */
export const MediaPicker = ({ open, onClose, onSelect, kind = "" }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/85 z-[100] overflow-y-auto" data-testid="media-picker">
      <div className="min-h-full flex items-start justify-center py-10 px-4">
        <div className="bg-[#0A0A0A] border border-[color:var(--gold)]/30 w-full max-w-6xl">
          <div className="flex items-center justify-between border-b border-white/10 p-5">
            <div>
              <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-1">Médiathèque</p>
              <p className="font-serif text-2xl">Choisir un média</p>
            </div>
            <button onClick={onClose} className="text-white/60 hover:text-white p-2" data-testid="picker-close"><X size={18} /></button>
          </div>
          <div className="p-6">
            <MediaLibrary picker onSelect={(url) => { onSelect?.(url); onClose?.(); }} filterKind={kind} />
          </div>
        </div>
      </div>
    </div>
  );
};
